<template>
  <div data-testid="store-select-button">
    <div
      class="w-full rounded-md py-2 store-select-shadow flex items-center font-semibold text-typography-secondary cursor-pointer"
      role="button"
      :class="{
        'justify-between px-4': !collapsed,
        'justify-center': collapsed,
      }"
      @click="handleStoreSelectClick"
    >
      <!-- company logo -->
      <div
        v-if="currentStore"
        class="flex gap-2 text-sm items-center w-full"
        :class="{
          'justify-center': collapsed,
        }"
      >
        <!-- Account Admin scope is the Parent Account: show an initials chip, not a stale Brand logo (ENG-5850). -->
        <div
          v-if="showAccountChip"
          class="flex h-6 w-6 flex-none items-center justify-center rounded bg-primary-700 text-2xs font-semibold text-neutral-white"
        >
          {{ accountInitials }}
        </div>
        <div
          v-else-if="!multipleStoresSelected && !allSelectedCategory"
          class="rounded h-6 w-6 border-neutral-300 border flex items-center justify-center flex-none object-cover bg-cover"
          :class="{
            'bg-neutral-300': !storeUrl,
          }"
        >
          <ClientOnly>
            <img v-if="storeUrl" :src="storeUrl" />
          </ClientOnly>
        </div>

        <span v-show="!collapsed" class="max-w-[180px] truncate text-xsm">
          {{ displayTitle }}
        </span>
      </div>
      <div v-else>
        <span class="text-xsm">Select a store</span>
      </div>
      <ChevronRightIcon v-show="!collapsed" v-if="allStores?.length > 1 || !currentStore" class="h-3 w-3" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChevronRightIcon } from '@heroicons/vue/24/outline';
import { storeToRefs } from 'pinia';

import { useAccountHierarchy } from '~/composables/useAccountHierarchy';
import { useAccountName } from '~/composables/useAccountName';
import { useMerchantStore } from '~/store/merchant';

const store = useMerchantStore();
const allStores = store.allStores;

const { allSelectedCategory, multipleStoresSelected, currentStore } = storeToRefs(store);

const route = useRoute();
const { isParent } = useAccountHierarchy();
const { accountName, load: loadAccountName } = useAccountName();

/** Account Admin pages are account-scoped, so the button shows the Parent Account, not a stale Brand (ENG-5850). */
const onAccountAdminRoute = computed(() => route.path?.startsWith('/account-admin') === true);

watch(
  () => onAccountAdminRoute.value && isParent.value,
  needsAccountName => {
    if (needsAccountName && !accountName.value) loadAccountName();
  },
  { immediate: true },
);

const displayTitle = computed(() => {
  if (onAccountAdminRoute.value && accountName.value) {
    return accountName.value;
  }

  if (!currentStore.value) {
    return 'Select a store';
  }

  if (allSelectedCategory.value) {
    return `All ${allSelectedCategory.value} Stores`;
  }

  if (multipleStoresSelected.value) {
    return 'Multiple selected';
  }

  return currentStore.value?.name;
});

const showAccountChip = computed(() => onAccountAdminRoute.value && isParent.value && !!accountName.value);

const accountInitials = computed(() =>
  accountName.value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0]?.toUpperCase())
    .join(''),
);

const storeUrl = computed(() => {
  return currentStore.value.logo || currentStore.value?.storeSettings?.storeLogoUrl;
});

const emit = defineEmits(['store-select:click']);

const handleStoreSelectClick = () => {
  if (allStores?.value?.length <= 1) {
    return;
  }

  emit('store-select:click');
};

const collapsed = inject('collapsed', true);
</script>

<style>
.store-select-shadow {
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2);
}
</style>
